'use client';

import { useCallback, useRef, useState } from 'react';

interface UseImageZoomPanOptions {
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
}

interface UseImageZoomPanReturn {
  zoomLevel: number;
  setZoomLevel: React.Dispatch<React.SetStateAction<number>>;
  panOffset: { x: number; y: number };
  isDragging: boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  reset: () => void;
  pointerHandlers: {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
  };
}

export function useImageZoomPan(
  options: UseImageZoomPanOptions = {}
): UseImageZoomPanReturn {
  const { minZoom = 25, maxZoom = 300, zoomStep = 25 } = options;

  const [zoomLevel, setZoomLevel] = useState(100);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  const zoomIn = useCallback(() => {
    setZoomLevel((z) => Math.min(maxZoom, z + zoomStep));
  }, [maxZoom, zoomStep]);

  const zoomOut = useCallback(() => {
    setZoomLevel((z) => {
      const next = Math.max(minZoom, z - zoomStep);
      if (next === 100) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  }, [minZoom, zoomStep]);

  const resetZoom = useCallback(() => {
    setZoomLevel(100);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const reset = useCallback(() => {
    setZoomLevel(100);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (zoomLevel <= 100) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsDragging(true);
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    },
    [zoomLevel]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging || !lastPointerRef.current) return;
      const dx = e.clientX - lastPointerRef.current.x;
      const dy = e.clientY - lastPointerRef.current.y;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    },
    [isDragging]
  );

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    lastPointerRef.current = null;
  }, []);

  return {
    zoomLevel,
    setZoomLevel,
    panOffset,
    isDragging,
    zoomIn,
    zoomOut,
    resetZoom,
    reset,
    pointerHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerLeave: handlePointerUp
    }
  };
}
